import { elementOf } from './anchoring';

// Comment marks are painted with the CSS Custom Highlight API, so the
// report's DOM is never changed to draw them. marks.module.scss styles these
// names.
export const MARKS = 'report-comment-mark';
export const FADED_MARKS = 'report-comment-mark-faded';
export const ACTIVE_MARKS = 'report-comment-mark-active';

// Chrome 105+, Safari 17.2+, Firefox 140+. Other browsers get no marks.
const canPaintMarks = () =>
    typeof CSS !== 'undefined' &&
    !!CSS.highlights &&
    typeof Highlight === 'function';

// Paints every placed comment, and the active comments and the draft on top.
// While the reader is on some of the text, every other mark fades under it.
// Returns what takes the marks away again.
export const paintMarks = (ranges, activeRanges, fadesOthers) => {
    if (!canPaintMarks()) return () => {};
    const { highlights } = CSS;
    const faded = new Highlight(...(fadesOthers ? ranges : []));
    faded.priority = 1;
    const active = new Highlight(...activeRanges);
    active.priority = 2;
    highlights.set(MARKS, new Highlight(...ranges));
    highlights.set(FADED_MARKS, faded);
    highlights.set(ACTIVE_MARKS, active);
    return () => {
        highlights.delete(MARKS);
        highlights.delete(FADED_MARKS);
        highlights.delete(ACTIVE_MARKS);
    };
};

// The text position under a point in the viewport. Safari only has the
// WebKit lookup.
const caretAt = (x, y) => {
    if (document.caretPositionFromPoint) {
        const position = document.caretPositionFromPoint(x, y);
        return (
            position && { node: position.offsetNode, offset: position.offset }
        );
    }
    const range = document.caretRangeFromPoint?.(x, y);
    return range && { node: range.startContainer, offset: range.startOffset };
};

// The lookup finds the nearest text position, which can be the end of a mark
// when the point is in the space after a line.
const isOnText = (range, x, y) =>
    Array.from(range.getClientRects()).some(
        (box) =>
            box.left <= x && x <= box.right && box.top <= y && y <= box.bottom
    );

// The ids of the placed comments whose marks cover a point in the viewport,
// in the order they were placed. A mark isn't an element, so what was clicked
// is found through the text under the point.
export const commentsMarkedAt = (placedRanges, x, y) => {
    const caret = caretAt(x, y);
    if (!caret) return [];
    return Array.from(placedRanges)
        .filter(
            ([, range]) =>
                range.isPointInRange(caret.node, caret.offset) &&
                isOnText(range, x, y)
        )
        .map(([id]) => id);
};

// Everything that scrolls the node up and down, closest first, ending with
// the window. From 1080px the article scrolls in a container of its own, and
// a tall table scrolls inside that.
const scrollersOf = (node) => {
    const scrollers = [];
    let element = elementOf(node);
    while (element) {
        const { overflowY } = getComputedStyle(element);
        if (
            /auto|scroll|overlay/.test(overflowY) &&
            element.scrollHeight > element.clientHeight
        ) {
            scrollers.push(element);
        }
        element = element.parentElement;
    }
    return [...scrollers, window];
};

// The part of the viewport the node can be seen in, inside everything that
// scrolls it.
export const visibleBounds = (node) =>
    scrollersOf(node).reduce(
        (bounds, scroller) => {
            const { top, bottom } =
                scroller === window
                    ? { top: 0, bottom: window.innerHeight }
                    : scroller.getBoundingClientRect();
            return {
                top: Math.max(bounds.top, top),
                bottom: Math.min(bounds.bottom, bottom),
            };
        },
        { top: -Infinity, bottom: Infinity }
    );

export const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

// Scrolls the text in the range to the middle of everything that scrolls it,
// as scrollIntoView does for an element. Its start goes to the top when it is
// too tall to fit. Works on the range itself, since a mark isn't an element
// to scroll into view.
//
// The scrolling is smooth, so nothing has moved yet when the next scroller
// out is scrolled. Where the text will be is worked out instead, which needs
// to know how far each scroller can actually go. The window goes last, so how
// far it can go doesn't matter.
export const scrollToRange = (range) => {
    const text = range.getBoundingClientRect();
    let { top } = text;
    for (const scroller of scrollersOf(range.startContainer)) {
        const isWindow = scroller === window;
        const view = isWindow
            ? { top: 0, height: window.innerHeight }
            : scroller.getBoundingClientRect();
        const margin = Math.max(0, (view.height - text.height) / 2);
        let by = top - view.top - margin;
        if (!isWindow) {
            const { scrollTop, scrollHeight, clientHeight } = scroller;
            by = clamp(by, -scrollTop, scrollHeight - clientHeight - scrollTop);
        }
        scroller.scrollBy({ top: by, behavior: 'smooth' });
        top -= by;
    }
};

// Scrolls the window to bring the text in the range above `bottom`, where
// something fixed to the bottom of the screen starts, in the middle of the
// room left. Its start goes to the top when it is too tall to fit. Text that
// is in view already stays where it is.
export const scrollAbove = (range, bottom) => {
    const text = range.getBoundingClientRect();
    if (text.top >= 0 && text.bottom <= bottom) return;
    const margin = Math.max(0, (bottom - text.height) / 2);
    window.scrollBy({ top: text.top - margin, behavior: 'smooth' });
};
